export const collectUniquePropertyIds = (tasks) => [
  ...new Set(
    (tasks || [])
      .map((task) => task.property)
      .filter((id) => id != null),
  ),
];

export function createPropertySummaryStore() {
  const loaded = new Map();
  const inflight = new Map();
  let generation = 0;

  async function loadProperty(propertyId, fetchProperty, { signal } = {}) {
    const gen = generation;
    const cached = loaded.get(propertyId);
    if (cached) {
      return cached;
    }

    const active = inflight.get(propertyId);
    if (active) {
      return active;
    }

    const promise = (async () => {
      try {
        const data = await fetchProperty(propertyId);
        if (signal?.aborted || gen !== generation) {
          throw new DOMException("Aborted", "AbortError");
        }
        const entry = { status: "loaded", data };
        loaded.set(propertyId, entry);
        return entry;
      } catch (error) {
        if (signal?.aborted || gen !== generation) {
          throw error;
        }
        const entry = { status: "error", error };
        loaded.set(propertyId, entry);
        return entry;
      } finally {
        inflight.delete(propertyId);
      }
    })();

    inflight.set(propertyId, promise);
    return promise;
  }

  async function loadForPropertyIds(propertyIds, fetchProperty, { signal } = {}) {
    const uniqueIds = [...new Set((propertyIds || []).filter((id) => id != null))];
    await Promise.all(
      uniqueIds.map((propertyId) => loadProperty(propertyId, fetchProperty, { signal })),
    );
    return new Map(uniqueIds.map((id) => [id, loaded.get(id)]).filter(([, entry]) => entry));
  }

  async function loadForTasks(tasks, fetchProperty, { signal } = {}) {
    return loadForPropertyIds(collectUniquePropertyIds(tasks), fetchProperty, { signal });
  }

  function get(propertyId) {
    return loaded.get(propertyId);
  }

  function reset() {
    generation += 1;
    loaded.clear();
    inflight.clear();
  }

  return {
    loadProperty,
    loadForPropertyIds,
    loadForTasks,
    get,
    reset,
  };
}

export async function loadPropertySummariesForTasks(
  tasks,
  fetchProperty,
  { signal, store } = {},
) {
  const activeStore = store || createPropertySummaryStore();
  return activeStore.loadForTasks(tasks, fetchProperty, { signal });
}
